import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import moment from "moment";

import { Button, Input, Field, Control, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useClickOutside } from "./hooks";

import "./CalendarInput.scss";

import { DAYS_OF_WEEK, generateDates, dateToKey } from "./utils";

import CalendarInputDay from "./CalendarInputDay";
import CalendarInputHeader from "./CalendarInputHeader";

const CalendarInput = ({
  startDate = new Date(),
  weeks = 4,
  value = [],
  onChange = console.log
}) => {
  const [show, setShow] = useState(false);
  const calendar = useRef();
  const [state, setState] = useState([]);

  useClickOutside(calendar, () => {
    onChange(state);
    setShow(false);
  });

  const [innerDate, setInnerDate] = useState(moment(startDate));

  const dates = useMemo(() => generateDates(innerDate, weeks), [
    innerDate,
    weeks
  ]);

  const handleChange = day => {
    const key = dateToKey(day);
    const currState = [...state];
    const idx = currState.indexOf(key);
    if (idx > -1) {
      currState.splice(idx, 1);
    } else {
      currState.push(key);
    }
    setState(currState);

    onChange(currState);
  };

  const handleSetMonth = dir => () => {
    setInnerDate(moment(innerDate).add(dir, "months"));
  };

  useEffect(() => {
    if (value) {
      setState(value);
    }
  }, [value]);

  const displayString = useMemo(() => {
    const currState = [...state];
    const firstValue = currState.shift();
    const lastValue = currState.pop();

    if (firstValue && lastValue) {
      return `${firstValue.slice(5)} to ${lastValue.slice(5)}`;
    }
    if (firstValue) {
      return `${firstValue.slice(5)}`;
    }
    return "";
  }, [state]);

  return (
    <div className="calendar-date-select" ref={calendar}>
      <Field kind="addons">
        <Control>
          <Input disabled type="text" value={displayString} />
        </Control>
        <Control>
          <Button
            color="primary"
            style={{ paddingLeft: 15, paddingRight: 15 }}
            type="button"
            onClick={() => setShow(!show)}
          >
            <Icon>
              <FontAwesomeIcon icon={show ? "times" : "plus"} />
            </Icon>
          </Button>
        </Control>
      </Field>
      {show && (
        <div className="calendar-date-select-input">
          <CalendarInputHeader
            date={innerDate}
            handleSetMonth={handleSetMonth}
          />
          <div className="calendar">
            {DAYS_OF_WEEK.map((d, i) => (
              <div className="calendar-day-label" key={i}>
                {d}
              </div>
            ))}
            {dates.map(d => (
              <CalendarInputDay
                day={moment(d)}
                key={dateToKey(d)}
                selected={state.includes(dateToKey(d))}
                onChange={handleChange}
              />
            ))}
          </div>
          {/* <pre>
          <code>{JSON.stringify(state, null, 2)}</code>
        </pre> */}
        </div>
      )}
    </div>
  );
};
CalendarInput.propTypes = {
  startDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment)
  ]),
  weeks: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.array
};

export default CalendarInput;
